import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Ticket, 
  Trash2, 
  Edit2, 
  Copy, 
  TrendingUp, 
  Users, 
  Calendar,
  Clock,
  Gift,
  BarChart3,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromoCodeAdmin = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [newCode, setNewCode] = useState({
    code: '',
    description: '',
    trial_days: 30,
    max_uses: '',
    expires_at: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [codesRes, analyticsRes] = await Promise.all([
        apiFetch('/api/promo-codes/list?include_inactive=true&include_expired=true'),
        apiFetch('/api/promo-codes/analytics')
      ]);

      if (codesRes.data) setPromoCodes(codesRes.data);
      if (analyticsRes.data) setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load promo codes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCode = async () => {
    if (!newCode.code || !newCode.description || !newCode.trial_days) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await apiFetch('/api/promo-codes/create', {
        method: 'POST',
        body: {
          code: newCode.code.toUpperCase(),
          description: newCode.description,
          trial_days: parseInt(newCode.trial_days),
          max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
          expires_at: newCode.expires_at || null,
          is_active: newCode.is_active
        }
      });

      if (error) {
        throw new Error(error || 'Failed to create promo code');
      }

      toast.success(`Promo code ${newCode.code.toUpperCase()} created!`);
      setShowCreateModal(false);
      setNewCode({ code: '', description: '', trial_days: 30, max_uses: '', expires_at: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCode = async () => {
    if (!selectedCode) return;

    setIsSubmitting(true);
    try {
      const { error } = await apiFetch(`/api/promo-codes/update/${selectedCode.id}`, {
        method: 'PUT',
        body: {
          description: selectedCode.description,
          trial_days: parseInt(selectedCode.trial_days),
          max_uses: selectedCode.max_uses ? parseInt(selectedCode.max_uses) : null,
          expires_at: selectedCode.expires_at || null,
          is_active: selectedCode.is_active
        }
      });

      if (error) {
        throw new Error(error || 'Failed to update promo code');
      }

      toast.success('Promo code updated!');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCode = async () => {
    if (!selectedCode) return;

    try {
      const { error } = await apiFetch(`/api/promo-codes/delete/${selectedCode.id}`, {
        method: 'DELETE'
      });

      if (error) {
        throw new Error(error || 'Failed to delete promo code');
      }

      toast.success('Promo code deleted');
      setShowDeleteDialog(false);
      setSelectedCode(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code} to clipboard`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs">Inactive</span>;
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">Expired</span>;
    }
    if (code.max_uses && code.times_used >= code.max_uses) {
      return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">Maxed Out</span>;
    }
    return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Active</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="h-7 w-7 text-primary" />
            Promo Codes
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Create and manage promotional codes for VIP subscriptions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-promo-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Code
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Codes</p>
                  <p className="text-2xl font-bold">{analytics.summary.total_codes}</p>
                </div>
                <Ticket className="h-8 w-8 text-primary/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Redemptions</p>
                  <p className="text-2xl font-bold">{analytics.summary.total_redemptions}</p>
                </div>
                <Users className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Last 30 Days</p>
                  <p className="text-2xl font-bold">{analytics.summary.recent_redemptions_30d}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Trial Days Given</p>
                  <p className="text-2xl font-bold">{analytics.summary.total_trial_days_given}</p>
                </div>
                <Gift className="h-8 w-8 text-purple-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Promo Codes Table */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            All Promo Codes ({promoCodes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : promoCodes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-left text-sm text-muted-foreground">
                    <th className="py-3 px-4">Code</th>
                    <th className="py-3 px-4">Description</th>
                    <th className="py-3 px-4">Trial Days</th>
                    <th className="py-3 px-4">Uses</th>
                    <th className="py-3 px-4">Expires</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map(code => (
                    <tr key={code.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <code className="bg-primary/10 text-primary px-2 py-1 rounded font-mono text-sm">
                            {code.code}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => copyToClipboard(code.code)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm max-w-[200px] truncate">{code.description}</td>
                      <td className="py-3 px-4">
                        <span className="text-primary font-semibold">{code.trial_days} days</span>
                      </td>
                      <td className="py-3 px-4">
                        {code.times_used}{code.max_uses ? ` / ${code.max_uses}` : ''}
                      </td>
                      <td className="py-3 px-4 text-sm">{formatDate(code.expires_at)}</td>
                      <td className="py-3 px-4">{getStatusBadge(code)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setSelectedCode({...code});
                              setShowEditModal(true);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-400"
                            onClick={() => {
                              setSelectedCode(code);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No promo codes yet</p>
              <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                Create Your First Code
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
            <DialogDescription>
              Create a new promotional code for VIP subscriptions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                placeholder="e.g., SUMMER2026"
                value={newCode.code}
                onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                placeholder="e.g., Summer Special - 2 months free"
                value={newCode.description}
                onChange={(e) => setNewCode({...newCode, description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="trial_days">Trial Days *</Label>
                <Input
                  id="trial_days"
                  type="number"
                  min="1"
                  placeholder="30"
                  value={newCode.trial_days}
                  onChange={(e) => setNewCode({...newCode, trial_days: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_uses">Max Uses (optional)</Label>
                <Input
                  id="max_uses"
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({...newCode, max_uses: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expires_at">Expiration Date (optional)</Label>
              <Input
                id="expires_at"
                type="datetime-local"
                value={newCode.expires_at}
                onChange={(e) => setNewCode({...newCode, expires_at: e.target.value})}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={newCode.is_active}
                onCheckedChange={(checked) => setNewCode({...newCode, is_active: checked})}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateCode} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Promo Code</DialogTitle>
            <DialogDescription>
              Update settings for <code className="bg-primary/10 px-2 py-1 rounded">{selectedCode?.code}</code>
            </DialogDescription>
          </DialogHeader>
          {selectedCode && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit_description">Description</Label>
                <Input
                  id="edit_description"
                  value={selectedCode.description}
                  onChange={(e) => setSelectedCode({...selectedCode, description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_trial_days">Trial Days</Label>
                  <Input
                    id="edit_trial_days"
                    type="number"
                    min="1"
                    value={selectedCode.trial_days}
                    onChange={(e) => setSelectedCode({...selectedCode, trial_days: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_max_uses">Max Uses</Label>
                  <Input
                    id="edit_max_uses"
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={selectedCode.max_uses || ''}
                    onChange={(e) => setSelectedCode({...selectedCode, max_uses: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_expires_at">Expiration Date</Label>
                <Input
                  id="edit_expires_at"
                  type="datetime-local"
                  value={selectedCode.expires_at ? selectedCode.expires_at.slice(0, 16) : ''}
                  onChange={(e) => setSelectedCode({...selectedCode, expires_at: e.target.value})}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit_is_active"
                  checked={selectedCode.is_active}
                  onCheckedChange={(checked) => setSelectedCode({...selectedCode, is_active: checked})}
                />
                <Label htmlFor="edit_is_active">Active</Label>
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Times used: <span className="font-semibold text-foreground">{selectedCode.times_used}</span>
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateCode} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Promo Code?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <code className="bg-red-500/10 text-red-400 px-2 py-1 rounded">{selectedCode?.code}</code>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCode} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PromoCodeAdmin;
